<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  createBriefingFromPack,
  listBriefingsForPack,
  type WorkPackRow,
} from "@/api/client";
import { hasTaskBrief } from "@/lib/workPackSections";

const props = defineProps<{ pack: WorkPackRow }>();

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const packId = computed(() => props.pack.id);

const { data: briefings, isPending } = useQuery({
  queryKey: ["pack-briefings", packId],
  queryFn: () => listBriefingsForPack(packId.value),
});

const briefReady = computed(() => hasTaskBrief(props.pack));
const busy = ref(false);
const errorMsg = ref<string | null>(null);

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function open(id: string): void {
  void router.push({
    name: "briefing-detail",
    params: { projectId: route.params.projectId, id },
  });
}

async function start(): Promise<void> {
  busy.value = true;
  errorMsg.value = null;
  try {
    const id = await createBriefingFromPack(props.pack);
    await queryClient.invalidateQueries({ queryKey: ["pack-briefings", packId] });
    open(id);
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-5">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="font-semibold">
          Site briefing &amp; sign-on
        </h2>
        <p class="text-sm text-neutral-400">
          Deliver the Task Brief on site — the gang signs on against it.
        </p>
      </div>
      <button
        v-if="(briefings?.length ?? 0) === 0"
        type="button"
        class="shrink-0 rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on hover:opacity-90 disabled:opacity-50"
        :disabled="busy || !briefReady"
        :title="briefReady ? '' : 'Build the Task Brief first'"
        @click="start"
      >
        {{ busy ? "Starting…" : "Start site briefing" }}
      </button>
    </div>

    <p
      v-if="!briefReady && (briefings?.length ?? 0) === 0"
      class="mt-2 text-sm text-neutral-400"
    >
      Build the Task Brief above first, then start the briefing.
    </p>
    <p
      v-if="errorMsg"
      class="mt-2 text-sm text-semantic-danger"
    >
      {{ errorMsg }}
    </p>

    <div
      v-if="isPending"
      class="mt-3 text-sm text-neutral-400"
    >
      Loading…
    </div>
    <ul
      v-else-if="briefings?.length"
      class="mt-3 grid gap-2"
    >
      <li
        v-for="b in briefings"
        :key="b.id"
        class="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-2.5 text-sm hover:bg-neutral-50"
        @click="open(b.id)"
      >
        <span>
          <span class="font-medium text-neutral-900">Briefing — {{ fmt(b.briefing_date) }}</span>
          <span class="text-neutral-500">
            · {{ b.signon_count }} signed on
          </span>
        </span>
        <span class="flex items-center gap-2">
          <span
            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="b.delivered
              ? 'bg-semantic-success/10 text-semantic-success'
              : 'bg-semantic-warning/10 text-semantic-warning'"
          >{{ b.delivered ? "Delivered" : "Not delivered" }}</span>
          <span
            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="b.status === 'closed'
              ? 'bg-neutral-100 text-neutral-600'
              : 'bg-brand-primary/10 text-brand-primary'"
          >{{ b.status === "closed" ? "Closed" : "Open" }}</span>
        </span>
      </li>
    </ul>
  </section>
</template>
